import { AppState } from "../../contexts/App";
import { Site } from "../../model/site";
import { ApiSiteResponse } from "../../services/api";

/**
 * The Keepo being edited lives only in memory, so a reload (F5) used to drop the person on /sites. It's kept
 * per tab (sessionStorage: another tab edits its own Keepo) to be reopened, the same version it was.
 */
const KEY = "Keepo.editingSite";

export type EditingSite = { siteId: string; usingDraft: boolean };

export const rememberEditingSite = (editing: EditingSite): void => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(editing));
  } catch {
    // no storage (private mode, blocked): a reload just goes back to the list, as before
  }
};

export const forgetEditingSite = (): void => {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // nothing kept
  }
};

export const readEditingSite = (): EditingSite | undefined => {
  try {
    const value = JSON.parse(sessionStorage.getItem(KEY) || "null");
    return value && typeof value.siteId === "string"
      ? { siteId: value.siteId, usingDraft: !!value.usingDraft }
      : undefined;
  } catch {
    return undefined;
  }
};

/**
 * The Keepo kept by `rememberEditingSite`, from the account's Keepos (GET /sites), as opening its card sets it
 * (Sites' `edit`): its unpublished changes when those were being edited (no "recover changes?" again), else the
 * published one. Undefined when it's gone (deleted, or another account in this tab).
 */
export const restoreEditingSite = (
  sites: ApiSiteResponse[],
  editing: EditingSite
): { site: Site; appState: Partial<AppState> } | undefined => {
  const found = sites.find(({ siteId }) => siteId === editing.siteId);
  if (!found || !(found.site || found.draft)) {
    return undefined;
  }

  const useDraft = !!found.draft && (editing.usingDraft || !found.site);

  return {
    site: useDraft ? found.draft : found.site,
    appState: {
      editingSiteId: found.siteId,
      editingSiteModified: useDraft,
      published: !!found.site,
      editingSiteDisabled: found.disabled,
      notRedirectOnEmptySites: true,
      editingSiteSubscription: found.subscription || { type: "FREE" },
      editingSiteTrackingIntegrations: found.tracking,
      editingSitePasswordProtected: !!found.passwordProtected,
      editingSiteAgeGated: !!found.ageGate?.enabled,
    },
  };
};
